import React, { useState, useEffect } from 'react';
import { 
  Radio, Users, Mic, Volume2, ShieldAlert, RefreshCw, 
  Search, AlertTriangle, CheckCircle, StopCircle, Globe
} from 'lucide-react';
import { managerAPI } from '../utils/api';

const ManagerRooms = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState('India');
  const [actionLoading, setActionLoading] = useState(false);
  const [search, setSearch] = useState('');

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const res = await managerAPI.getRooms();
      if (res.data?.success) {
        setRooms(res.data.rooms || []);
        if (res.data.country) setCountry(res.data.country);
      }
    } catch (err) {
      console.error("Failed to load rooms:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  const handleForceClose = async (room) => {
    if (!window.confirm(`Are you sure you want to FORCE CLOSE live room "${room.title || room.name || room.room_id}"? This action is logged.`)) {
      return;
    }

    try {
      setActionLoading(true);
      const res = await managerAPI.closeRoom(room.id);
      alert(res.data?.message || "Room terminated successfully.");
      fetchRooms();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to terminate room.");
    } finally {
      setActionLoading(false);
    }
  };

  const filteredRooms = rooms.filter(r => {
    const term = search.toLowerCase();
    const title = (r.title || r.name || '').toLowerCase();
    const ownerName = `${r.first_name || ''} ${r.last_name || ''}`.toLowerCase();
    const xoxoId = (r.xoxo_id || '').toLowerCase();
    return title.includes(term) || ownerName.includes(term) || xoxoId.includes(term);
  });

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-white/5">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-extrabold text-white tracking-tight">Active Live Rooms</h1>
            <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-bold text-xs uppercase flex items-center gap-1.5">
              <Globe size={13} /> {country} Locked
            </span>
          </div>
          <p className="text-slate-400 text-sm mt-1">
            Real-time audio rooms currently broadcasted in your assigned country. You have authority to force close violating rooms.
          </p>
        </div>

        <button 
          onClick={fetchRooms}
          className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-slate-300 font-semibold text-sm transition-colors cursor-pointer w-max"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          <span>Refresh Live</span>
        </button>
      </div>

      {/* Filter / Search Bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <input
          type="text"
          placeholder="Search active rooms by title or host..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-[#0b0f19] border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition-colors"
        />
      </div>

      {/* Room Grid */}
      {loading ? (
        <div className="py-20 flex flex-col items-center justify-center text-slate-400">
          <RefreshCw size={24} className="animate-spin text-indigo-400 mb-3" />
          <p>Scanning active rooms in {country}...</p>
        </div>
      ) : filteredRooms.length === 0 ? (
        <div className="py-20 text-center bg-[#0b0f19] border border-white/5 rounded-2xl p-8">
          <Radio size={36} className="text-slate-600 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-white mb-1">No Active Rooms Found</h3>
          <p className="text-sm text-slate-400">There are currently no active rooms hosted in {country}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRooms.map((room) => (
            <div 
              key={room.id}
              className="bg-[#0b0f19] border border-white/5 rounded-2xl p-6 relative overflow-hidden flex flex-col justify-between hover:border-indigo-500/30 transition-all shadow-xl group"
            >
              {/* Room Top info */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-bold animate-pulse">
                    <Radio size={12} /> LIVE NOW
                  </span>
                  <span className="text-xs font-mono text-slate-400">ID: {room.room_id || room.id}</span>
                </div>

                <h3 className="text-lg font-black text-white line-clamp-1 mb-2">
                  {room.title || room.name || 'Untitled Room'}
                </h3>

                {/* Host Info */}
                <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5 mb-4">
                  <img 
                    src={room.avatar_url || `https://api.dicebear.com/7.x/bottts/svg?seed=${room.xoxo_id || 'host'}`} 
                    alt="Host" 
                    className="w-10 h-10 rounded-full object-cover border border-white/10"
                  />
                  <div>
                    <div className="font-bold text-sm text-white">{room.first_name || 'Room Host'} {room.last_name || ''}</div>
                    <div className="text-xs text-indigo-400 font-mono">XOXO: {room.xoxo_id || 'N/A'}</div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5 flex items-center gap-2">
                    <Users size={16} className="text-indigo-400" />
                    <div>
                      <div className="text-[10px] text-slate-400 font-semibold uppercase">Audience</div>
                      <div className="text-sm font-black text-white">{room.total_listeners || room.listener_count || 1}</div>
                    </div>
                  </div>
                  <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5 flex items-center gap-2">
                    <Mic size={16} className="text-purple-400" />
                    <div>
                      <div className="text-[10px] text-slate-400 font-semibold uppercase">Speakers</div>
                      <div className="text-sm font-black text-white">{room.speaker_count || 1}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Force Close Button */}
              <button
                onClick={() => handleForceClose(room)}
                disabled={actionLoading}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-rose-600/10 hover:bg-rose-600 border border-rose-500/20 hover:border-rose-600 text-rose-300 hover:text-white text-xs font-bold transition-all cursor-pointer shadow-lg shadow-rose-900/10"
              >
                <StopCircle size={16} />
                <span>Force Terminate Room</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManagerRooms;
